import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useNasMount } from "../hooks/useNasMount";

type NasConfig = {
  username: string;
  password: string;
  serverPath: string;
  mountPoint: string;
};

type NasContextValue = {
  status: "idle" | "mounting" | "mounted" | "error";
  mountPoint?: string;
  error: string | null;
  config: NasConfig;
  setConfig: (cfg: NasConfig) => void;
  triggerMount: (cfg?: NasConfig) => Promise<void>;
};

const NasContext = createContext<NasContextValue | undefined>(undefined);

const STORAGE_KEY = "nasConfig";

const defaultConfig: NasConfig = {
  username: "",
  password: "",
  serverPath: "",
  mountPoint: ""
};

export function NasProvider({ children }: { children: React.ReactNode }) {
  const [config, setConfigState] = useState<NasConfig>(() => {
    const raw = typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
    if (raw) {
      try {
        return { ...defaultConfig, ...JSON.parse(raw) } as NasConfig;
      } catch {
        return defaultConfig;
      }
    }
    return defaultConfig;
  });

  const { status, mountPoint, error, mount } = useNasMount(config.mountPoint || undefined);

  const setConfig = (cfg: NasConfig) => {
    setConfigState(cfg);
    if (typeof window !== "undefined") {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(cfg));
    }
  };

  const triggerMount = async (cfg?: NasConfig) => {
    const nextCfg = cfg || config;
    setConfig(nextCfg);
    await mount({
      username: nextCfg.username,
      password: nextCfg.password,
      serverPath: nextCfg.serverPath,
      mountPoint: nextCfg.mountPoint
    });
  };

  useEffect(() => {
    // Auto-mount on startup if config exists.
    if (config.username && config.password && config.serverPath && config.mountPoint) {
      triggerMount().catch(() => {
        /* error handled in hook */
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(
    () => ({
      status,
      mountPoint,
      error,
      config,
      setConfig,
      triggerMount
    }),
    [status, mountPoint, error, config]
  );

  return <NasContext.Provider value={value}>{children}</NasContext.Provider>;
}

export function useNas() {
  const ctx = useContext(NasContext);
  if (!ctx) throw new Error("useNas must be used within NasProvider");
  return ctx;
}
